import React from 'react';
import { ChevronDown } from 'lucide-react';
import { FieldOption } from '../types/form.types';
import { FormTheme } from '../types/formTheme';

interface FormSelectProps {
    id: string;
    value: string | string[];
    onChange: (value: string | string[]) => void;
    onBlur?: () => void;
    options: FieldOption[];
    placeholder?: string;
    multiple?: boolean;
    required?: boolean;
    disabled?: boolean;
    error?: string;
    appearance?: FormTheme;
    className?: string;
}

const FormSelect: React.FC<FormSelectProps> = ({
    id,
    value,
    onChange,
    onBlur,
    options,
    placeholder = 'Select an option...',
    multiple = false,
    required = false,
    disabled = false,
    error,
    appearance = 'default',
    className = ''
}) => {
    const selectedValues = Array.isArray(value)
        ? value
        : typeof value === 'string' && value.length > 0
            ? [value]
            : [];

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        if (multiple) {
            const selectedValues = Array.from(e.target.selectedOptions).map((option) => option.value);
            onChange(selectedValues);
            return;
        }

        onChange(e.target.value);
    };

    const handleMultiToggle = (optionValue: string) => {
        if (!multiple) return;

        if (selectedValues.includes(optionValue)) {
            onChange(selectedValues.filter((value) => value !== optionValue));
            return;
        }

        onChange([...selectedValues, optionValue]);
    };

    if (multiple) {
        return (
            <div className={`space-y-3 ${className}`}>
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Select all that apply</p>
                <div className="space-y-2">
                    {options.map((option) => {
                        const optionId = `${id}-${option.value}`;
                        const isSelected = selectedValues.includes(option.value);

                        return (
                            <label
                                key={option.value}
                                htmlFor={optionId}
                                className={`
                                    ${appearance === 'tracking'
                                        ? 'flex items-center gap-3 p-4 rounded-lg bg-white border transition-all duration-200'
                                        : 'flex items-center gap-3 p-4 rounded-xl bg-white/80 backdrop-blur-sm border-2 transition-all duration-200'
                                    }
                                    cursor-pointer
                                    ${disabled ? 'opacity-50 cursor-not-allowed' : appearance === 'tracking' ? 'hover:border-gray-400' : 'hover:border-blue-300'}
                                    ${isSelected
                                        ? appearance === 'tracking'
                                            ? 'border-black bg-gray-50'
                                            : 'border-blue-500 bg-blue-50/50'
                                        : error
                                            ? 'border-red-400'
                                            : appearance === 'tracking'
                                                ? 'border-gray-300'
                                                : 'border-gray-200'
                                    }
                                `}
                            >
                                <input
                                    id={optionId}
                                    type="checkbox"
                                    checked={isSelected}
                                    disabled={disabled}
                                    onChange={() => handleMultiToggle(option.value)}
                                    className="sr-only"
                                />
                                <div
                                    className={`
                                        h-5 w-5 rounded border-2 transition-all duration-200 flex items-center justify-center
                                        ${isSelected
                                            ? appearance === 'tracking'
                                                ? 'border-black bg-black'
                                                : 'border-blue-500 bg-blue-500'
                                            : 'border-gray-300 bg-white'
                                        }
                                    `}
                                >
                                    {isSelected && <span className="text-xs text-white">✓</span>}
                                </div>
                                <span className="text-gray-900 font-medium">{option.label}</span>
                            </label>
                        );
                    })}
                </div>
            </div>
        );
    }

    return (
        <div className="relative">
            <select
                id={id}
                value={value}
                onChange={handleChange}
                onBlur={onBlur}
                multiple={multiple}
                required={required}
                disabled={disabled}
                className={`
                    ${appearance === 'tracking'
                        ? `
                            w-full px-4 py-3 rounded-lg appearance-none
                            bg-white border transition-all duration-200
                            text-gray-900
                            focus:outline-none focus:ring-2 focus:ring-offset-0
                            disabled:opacity-50 disabled:cursor-not-allowed
                        `
                        : `
                            w-full px-4 py-3 rounded-xl appearance-none
                            bg-white/80 backdrop-blur-sm
                            border-2 transition-all duration-200
                            text-gray-900
                            focus:outline-none focus:ring-2 focus:ring-offset-2
                            disabled:opacity-50 disabled:cursor-not-allowed
                        `
                    }
                    ${error
                        ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20'
                        : appearance === 'tracking'
                            ? 'border-gray-300 focus:border-black focus:ring-black/10 hover:border-gray-400'
                            : 'border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 hover:border-gray-300'
                    }
                    ${!value ? 'text-gray-400' : ''}
                    ${className}
                `}
                aria-invalid={error ? 'true' : 'false'}
                aria-describedby={error ? `${id}-error` : undefined}
            >
                {!multiple && (
                    <option value="" disabled>
                        {placeholder}
                    </option>
                )}
                {options.map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
            {!multiple && (
                <ChevronDown
                    className={`absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none ${appearance === 'tracking' ? 'text-gray-500' : 'text-gray-400'}`}
                    size={20}
                />
            )}
        </div>
    );
};

export default FormSelect;
